import { useId, useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { Icon } from './Icon.js';
import { cx } from './cx.js';

type InputProps = {
  label?: string;
  hint?: string;
  error?: string;
  /** Élément posé à droite dans le champ (bouton icône). */
  trailing?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>;

/**
 * Champ de saisie avec libellé visible, aide et message d'erreur.
 * L'erreur remplace l'aide, passe le contour en --color-danger et porte une
 * icône (jamais la couleur seule). Tous les attributs d'<input> passent.
 */
export function Input({ label, hint, error, trailing, className, id, ...rest }: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const helpId = `${inputId}-help`;
  const input = (
    <input
      {...rest}
      id={inputId}
      className={cx('pl-input', error && 'pl-input-error', trailing != null && 'pl-input-has-trailing')}
      aria-invalid={error ? true : undefined}
      aria-describedby={error || hint ? helpId : undefined}
    />
  );
  return (
    <div className={cx('pl-field', className)}>
      {label && <label className="pl-field-label" htmlFor={inputId}>{label}</label>}
      {trailing != null
        ? <div className="pl-input-wrap">{input}<span className="pl-input-trailing">{trailing}</span></div>
        : input}
      {error
        ? <div id={helpId} className="pl-field-error"><Icon name="alert" size={12} />{error}</div>
        : hint && <div id={helpId} className="pl-field-hint">{hint}</div>}
    </div>
  );
}

/** Texte sur plusieurs lignes — mêmes libellé, aide et erreur que Input. */
export function Textarea({ label, hint, error, className, id, ...rest }: {
  label?: string;
  hint?: string;
  error?: string;
} & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const helpId = `${inputId}-help`;
  return (
    <div className={cx('pl-field', className)}>
      {label && <label className="pl-field-label" htmlFor={inputId}>{label}</label>}
      <textarea
        {...rest}
        id={inputId}
        className={cx('pl-input', 'pl-textarea', error && 'pl-input-error')}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? helpId : undefined}
      />
      {error
        ? <div id={helpId} className="pl-field-error"><Icon name="alert" size={12} />{error}</div>
        : hint && <div id={helpId} className="pl-field-hint">{hint}</div>}
    </div>
  );
}

/** Champ mot de passe avec bouton afficher / masquer. */
export function PasswordInput(props: Omit<InputProps, 'type' | 'trailing'>) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      {...props}
      type={visible ? 'text' : 'password'}
      trailing={(
        <button
          type="button"
          className="pl-input-icon-btn"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          aria-pressed={visible}
        >
          <Icon name={visible ? 'eye-off' : 'eye'} size={16} />
        </button>
      )}
    />
  );
}
